import { CycleBoundaryDrawable, MeshPoint, SNAPPING_ERROR_TOLERANCE } from 'formwork-planner-lib'
import paper from 'paper/dist/paper-core'

/**
 * Represents a wall segment of the cycle which can have mesh points or cycle boundaries as end and start points
 */
export class CycleSegment {
  constructor(
    public startPoint: CycleBoundaryDrawable | MeshPoint,
    public endPoint: CycleBoundaryDrawable | MeshPoint
  ) {}

  equals(that: CycleSegment): boolean {
    return (
      (this.startPoint === that.startPoint && this.endPoint === that.endPoint) ||
      (this.startPoint === that.endPoint && this.endPoint === that.startPoint)
    )
  }

  /**
   * Checks if the given point is closer than the tolerance to the segment's middle line (line between the start and end point)
   * @param point the point to check for
   * @param tolerance maximum accepted distance from the middle line of the cycle segment
   */
  containsPoint(point: paper.Point, tolerance: number): boolean {
    const start: paper.Point =
      this.startPoint instanceof CycleBoundaryDrawable
        ? this.startPoint.getCenter()
        : this.startPoint
    const end: paper.Point =
      this.endPoint instanceof CycleBoundaryDrawable ? this.endPoint.getCenter() : this.endPoint
    const segmentLine = new paper.Path.Line(start, end)
    const nearestPoint = segmentLine.getNearestPoint(point)
    const segmentVector = end.subtract(start).normalize()
    const vectorToNearestPoint = nearestPoint.subtract(point)

    // see explanation: https://dev.azure.com/Umdasch-Group/Doka-ESD-EFP/_wiki/wikis/Doka-ESD-EFP.wiki/4283/Technical-Documentation?anchor=containspoint-(cyclesegment.ts)
    return (
      nearestPoint.isClose(point, tolerance) &&
      !(
        !vectorToNearestPoint.isOrthogonal(segmentVector) &&
        (nearestPoint.isClose(start, SNAPPING_ERROR_TOLERANCE) ||
          nearestPoint.isClose(end, SNAPPING_ERROR_TOLERANCE))
      )
    )
  }

  /**
   * Returns the center point of the cycle segment, used to place the cycle number on segments
   */
  getCenter(): paper.Point {
    const start: paper.Point =
      this.startPoint instanceof CycleBoundaryDrawable
        ? this.startPoint.getCenter()
        : this.startPoint
    const end: paper.Point =
      this.endPoint instanceof CycleBoundaryDrawable ? this.endPoint.getCenter() : this.endPoint
    return new paper.Point((start.x + end.x) / 2, (start.y + end.y) / 2)
  }

  getLength(): number {
    const startPoint =
      this.startPoint instanceof CycleBoundaryDrawable
        ? this.startPoint.getCenter()
        : this.startPoint
    const endPoint =
      this.endPoint instanceof CycleBoundaryDrawable ? this.endPoint.getCenter() : this.endPoint

    return startPoint.getDistance(endPoint)
  }
}
